import { Building2, TrendingDown, TrendingUp, Target } from 'lucide-react'

type branch = {
    address: string
    branch: string
    customers: number
    growth: number
    products: number
    profit: number
    sales: number
}

type branchMetrics = {
    sales: number
    profit: number
    customers: number
    products: number
    growth: number
}

type salesGoals = {
    dailyActual: number
    dailyGoal: number
    monthlyActual: number
    monthlyGoal: number
    annualActual: number
    annualGoal: number
}


export function SucursalCard({
    branch,
    branchMetrics,
    salesGoals,
    index
}: {
    branch: branch
    branchMetrics: branchMetrics[]
    salesGoals: salesGoals[]
    index: number
}) {
    const metrics = branchMetrics[index]
    const goals = salesGoals.find((g: any) => g.branch === branch.branch)

    if (!metrics) {
        return null
    }

    const dailyProgress = goals?.dailyGoal ? (goals.dailyActual / goals.dailyGoal) * 100 : 0
    const monthlyProgress = goals?.monthlyGoal ? (goals.monthlyActual / goals.monthlyGoal) * 100 : 0
    const annualProgress = goals?.annualGoal ? (goals.annualActual / goals.annualGoal) * 100 : 0

    return (
        <div
            key={branch.branch}
            className="rounded-lg border border-slate-200 bg-white p-6"
        >
            <div className="mb-4 flex items-start justify-between">
                <div>
                    <div className="mb-2 flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-slate-900">
                            {branch.branch}
                        </h3>
                    </div>

                </div>
            </div>

            <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                        Ventas del mes:
                    </span>
                    <span className="font-semibold text-slate-900">
                        ${metrics.sales.toLocaleString()}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Utilidad:</span>
                    <span className="font-semibold text-green-600">
                        ${metrics.profit.toLocaleString()}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Clientes:</span>
                    <span className="font-medium text-slate-900">
                        {metrics.customers}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                        Productos vendidos:
                    </span>
                    <span className="font-medium text-slate-900">
                        {metrics.products}
                    </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                    <span className="text-sm text-slate-600">Crecimiento:</span>
                    <div className="flex items-center gap-1">
                        {metrics.growth > 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                            <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                        <span
                            className={`font-semibold ${metrics.growth > 0 ? 'text-green-600' : 'text-red-600'
                                }`}
                        >
                            {metrics.growth > 0 ? '+' : ''}
                            {metrics.growth}%
                        </span>
                    </div>
                </div>
            </div>

            {/* Sales Goals Section */}
            <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                <div className="mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-purple-600" />
                    <h4 className="text-sm font-semibold text-slate-900">
                        Metas de Venta
                    </h4>
                </div>

                {/* Daily Goal */}
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">Diaria</span>
                        <span
                            className={`font-semibold ${dailyProgress >= 100 && goals?.dailyGoal ? 'text-green-600' : 'text-slate-600'}`}
                        >
                            {goals?.dailyGoal ? `$${goals.dailyActual.toLocaleString()} / $${goals.dailyGoal.toLocaleString()}` : 'No configurada'}
                        </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-200">
                        <div
                            className={`h-2 rounded-full ${dailyProgress >= 100 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(dailyProgress, 100)}%` }}
                        />
                    </div>
                    <p className="text-right text-xs text-slate-500">
                        {dailyProgress.toFixed(1)}%
                    </p>
                </div>

                {/* Monthly Goal */}
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">Mensual</span>
                        <span
                            className={`font-semibold ${monthlyProgress >= 100 && goals?.monthlyGoal ? 'text-green-600' : 'text-slate-600'}`}
                        >
                            {goals?.monthlyGoal ? `$${goals.monthlyActual.toLocaleString()} / $${goals.monthlyGoal.toLocaleString()}` : 'No configurada'}
                        </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-200">
                        <div
                            className={`h-2 rounded-full ${monthlyProgress >= 100 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(monthlyProgress, 100)}%` }}
                        />
                    </div>
                    <p className="text-right text-xs text-slate-500">
                        {monthlyProgress.toFixed(1)}%
                    </p>
                </div>

                {/* Annual Goal */}
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">Anual</span>
                        <span
                            className={`font-semibold ${annualProgress >= 100 && goals?.annualGoal ? 'text-green-600' : 'text-slate-600'}`}
                        >
                            {goals?.annualGoal ? `$${goals.annualActual.toLocaleString()} / $${goals.annualGoal.toLocaleString()}` : 'No configurada'}
                        </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-200">
                        <div
                            className={`h-2 rounded-full ${annualProgress >= 100 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(annualProgress, 100)}%` }}
                        />
                    </div>
                    <p className="text-right text-xs text-slate-500">
                        {annualProgress.toFixed(1)}%
                    </p>
                </div>
            </div>
        </div>
    )
}